import { z } from "zod"

import type { WaitingReason } from "./suggestions/schema"

/** How freely an agent may change what it runs with, each mode freer than the one before. Client-safe. */

export const SELF_LEARNING_MODES = ["off", "suggest", "automatic", "extended", "full"] as const
export const SelfLearningModeSchema = z.enum(SELF_LEARNING_MODES)
export type SelfLearningMode = z.infer<typeof SelfLearningModeSchema>

/**
 * The reasons a draft may wait for a person under each mode — the mode table. An agent without its own instructions
 * waits in every mode that drafts.
 */
export const WAITING_REASONS_BY_MODE: Record<SelfLearningMode, readonly WaitingReason[]> = {
  off: [],
  suggest: ["suggest", "after_rejection", "inherited"],
  automatic: ["your_text", "limit", "kb_article", "kb_limit", "kb_off", "after_rejection", "after_edit", "inherited"],
  extended: ["limit", "kb_limit", "kb_off", "after_rejection", "inherited"],
  full: ["kb_limit", "kb_off", "inherited"],
}

/** Whether the mode lets the agent change anything without a person; Off and Suggest only never do. */
export function appliesChanges(mode: SelfLearningMode): boolean {
  switch (mode) {
    case "off":
    case "suggest":
      return false
    case "automatic":
    case "extended":
    case "full":
      return true
    default: {
      const exhaustive: never = mode
      return exhaustive
    }
  }
}

/** An agent without its own instructions waits for a person in every mode, so a mode that applies acts as Suggest only. */
export function actsAsSuggestOnly(mode: SelfLearningMode, hasOwnInstructions: boolean | null): boolean {
  return hasOwnInstructions === false && appliesChanges(mode)
}

export interface LearningModeState {
  mode: SelfLearningMode;
  /** ISO. */
  since: string;
  /** Automatic's option: what it learns goes into the knowledge base without a person's OK. */
  addsArticles: boolean;
}
